// src/pages/quotation/QuotationList.jsx
// ✅ 용역 견적서 관리 [1단계] — 목록 페이지. 발주처(계약)별로 그룹, 펼치면 그
// 발주처의 견적서 버전 이력(v1~vN, 작성중/확정 배지)을 보여준다. 확정(final)
// 버전은 삭제 버튼 자체를 숨김(뷰 레이어에서도 403으로 막지만 UI에서부터 안 보이게).
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import { downloadFileWithAlert } from "../../utils/download";
import useBackdropClose from "../../hooks/useBackdropClose";
import { fmtMonths } from "./format";

const S = {
  wrap: { padding: 16, fontSize: 13, color: "#1f2937" },
  headerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 },
  title: { fontSize: 18, fontWeight: 700, margin: 0, whiteSpace: "nowrap" },
  newBtn: { width: 160, height: 36, background: "#0b5fff", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" },
  caseCard: { border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 10, overflow: "hidden" },
  caseHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "#f8fafc", cursor: "pointer" },
  caseTitle: { fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  caseMeta: { color: "#6b7280", fontSize: 12, whiteSpace: "nowrap" },
  // tableLayout: "fixed" — 지정한 컬럼 폭이 내용에 밀려 늘어나지 않게 한다.
  // ⚠️ minWidth — 고정폭 합(패딩 포함 1,092) + 변경사항 최소 140. 창이 이보다
  // 좁아지면 표가 줄어드는 대신 아래 tableScroll이 가로 스크롤을 준다.
  // 이게 없으면 좁은 창에서 맨 오른쪽 삭제 버튼이 caseCard의 overflow:hidden에
  // 잘려 조용히 사라진다(1024px 실측: 우측여백 -107px).
  versionTable: { width: "100%", minWidth: 1232, borderCollapse: "collapse", tableLayout: "fixed" },
  tableScroll: { overflowX: "auto" },
  th: { background: "#fff", padding: "6px 10px", borderBottom: "1px solid #e5e7eb", textAlign: "left", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" },
  td: { padding: "8px 10px", borderBottom: "1px solid #f1f5f9", fontSize: 13 },
  // ✅ 고정폭 컬럼 — 버전/상태/연도/인원·개월/수정일은 내용 길이가 정해져 있어
  // 먼저 폭을 못박고, 남는 폭을 품명·변경사항이 나눠 갖는다. 예전엔 전부
  // auto라 좁은 화면에서 "2명 / 12개월"과 "2026-08-21"이 두 줄로 꺾였다.
  // ⚠️ nowrap은 th·td 양쪽에 다 걸어야 한다 — 헤더만 걸면 본문이 꺾인다.
  colVersion: { width: 56, whiteSpace: "nowrap" },
  colStatus: { width: 72, whiteSpace: "nowrap" },
  colYear: { width: 60, whiteSpace: "nowrap" },
  colPersonnel: { width: 112, whiteSpace: "nowrap" },
  colUpdated: { width: 104, whiteSpace: "nowrap" },
  // ✅ 액션 컬럼 — 버튼 실폭 합에서 역산한다(추정 금지).
  //   편집 72 + 4(marginRight) + 엑셀 72 + 4 + 새버전 72 + 4 + 삭제 56 = 284
  //   + td 좌우 padding 10+10 = 304, 여기에 여유 4 → 308
  // ⚠️ 236px이던 시절 삭제 버튼이 잘렸다(2026-08-21). caseCard가 overflow:hidden
  // 이라 넘친 만큼 그대로 잘려 나간다 — 폭이 모자라면 조용히 안 보인다.
  colAction: { width: 308, whiteSpace: "nowrap" },
  // ✅ 품명 — 34%(가변)에서 고정 상한으로. 가변이면 넓은 화면에서 품명이 남는 폭을
  // 다 먹어 "품명↔인원/개월" 사이가 벌어지고 변경사항이 좁아졌다. 남는 폭은
  // 변경사항이 가져가는 게 원래 설계다. 넘치는 품명은 말줄임 + title 툴팁.
  colTitle: { width: 240 },
  badgeDraft: { display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: "#fef3c7", color: "#92400e", whiteSpace: "nowrap" },
  badgeFinal: { display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: "#d1fae5", color: "#065f46", whiteSpace: "nowrap" },
  // ⚠️ boxSizing/padding 명시 — 없으면 브라우저 UA 기본 padding(약 1px 6px)이
  // width 72에 더해져(content-box) 실폭이 86px쯤 된다. 액션 컬럼 폭을 버튼
  // 선언폭 합으로 계산하려면 선언폭 = 실폭이어야 한다.
  actionBtn: { width: 72, height: 28, boxSizing: "border-box", padding: 0, fontSize: 12, borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer", whiteSpace: "nowrap", marginRight: 4 },
  actionBtnPrimary: { width: 72, height: 28, boxSizing: "border-box", padding: 0, fontSize: 12, borderRadius: 6, border: "none", background: "#0b5fff", color: "#fff", cursor: "pointer", whiteSpace: "nowrap", marginRight: 4 },
  actionBtnDanger: { width: 56, height: 28, boxSizing: "border-box", padding: 0, fontSize: 12, borderRadius: 6, border: "1px solid #fca5a5", background: "#fff", color: "#dc2626", cursor: "pointer", whiteSpace: "nowrap" },
  // ⚠️ 전역 button{width:100%} 누수(CLAUDE.md)에 안 잡히도록 width/height를 명시한다.
  caseDeleteBtn: { width: 52, height: 26, fontSize: 12, borderRadius: 6, border: "1px solid #fca5a5", background: "#fff", color: "#dc2626", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 },
  caseDeleteBtnOff: { width: 52, height: 26, fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb", background: "#f9fafb", color: "#c0c4cc", cursor: "not-allowed", whiteSpace: "nowrap", flexShrink: 0 },
};

// 그 케이스의 확정(final) 버전 목록 — 삭제 가능 여부 판정 기준.
const finalVersions = (c) => (c.versions || []).filter((v) => v.status === "final");

export default function QuotationList() {
  const navigate = useNavigate();
  const location = useLocation();
  // ✅ 중복 안내 모달의 [견적서 목록으로 이동]이 넘겨준 케이스 — 펼치고 스크롤한 뒤
  // 잠깐 하이라이트한다. 라우팅 state로 받는다(쿼리스트링을 쓰면 새로고침 때마다
  // 하이라이트가 다시 켜진다).
  const focusCaseId = location.state?.caseId ?? null;
  const [highlightId, setHighlightId] = useState(null);
  const caseRefs = React.useRef({});
  const [cases, setCases] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("quotation/cases/");
      setCases(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 견적서 목록 불러오기 실패:", err);
    }
  };
  useEffect(() => { load(); }, []);

  // 목록이 그려진 뒤에 펼침·스크롤·하이라이트를 건다 — 마운트 직후엔 아직 카드가
  // 없어 scrollIntoView가 아무 데도 못 간다.
  useEffect(() => {
    // cases가 채워진 뒤에야 카드 ref가 생긴다.
    if (focusCaseId == null || cases.length === 0) return;
    setExpanded((prev) => ({ ...prev, [focusCaseId]: true }));
    setHighlightId(focusCaseId);
    const node = caseRefs.current[focusCaseId];
    if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(() => setHighlightId(null), 1800);
    // 뒤로가기·새로고침에서 다시 켜지지 않게 state를 비운다.
    navigate(".", { replace: true, state: null });
    return () => clearTimeout(timer);
  }, [focusCaseId, cases.length, navigate]);

  const toggle = (caseId) => setExpanded((prev) => ({ ...prev, [caseId]: !prev[caseId] }));

  // ✅ 새 버전은 변경사항 메모를 먼저 묻는다 — 선택 입력이라 비워도 그대로 생성된다.
  const [newVersionFor, setNewVersionFor] = useState(null);   // 대상 버전 id

  const createVersion = async (versionId, changeNote) => {
    setBusy(true);
    try {
      const { data } = await api.post(`quotation/versions/${versionId}/new_version/`,
                                      { change_note: changeNote });
      navigate(`/quotation/${data.id}/edit`);
    } catch (err) {
      alert("새 버전 생성 실패: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
      setNewVersionFor(null);
    }
  };

  // ✅ 변경사항 메모 저장 — 목록에서 바로. 성공하면 로컬 상태만 갈아끼운다
  // (목록 전체를 다시 부르면 펼쳐둔 카드가 접히고 스크롤이 튄다).
  const saveChangeNote = async (versionId, note) => {
    const { data } = await api.patch(`quotation/versions/${versionId}/change-note/`,
                                     { change_note: note });
    setCases((prev) => prev.map((c) => ({
      ...c,
      versions: (c.versions || []).map((v) =>
        (v.id === versionId ? { ...v, change_note: data.change_note } : v)),
    })));
    return data.change_note;
  };

  const handleDelete = async (versionId) => {
    if (!window.confirm("이 버전(작성중)을 삭제하시겠습니까? 되돌릴 수 없습니다.")) return;
    setBusy(true);
    try {
      await api.delete(`quotation/versions/${versionId}/`);
      await load();
    } catch (err) {
      alert("삭제 실패: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteCase = async (c) => {
    const versions = c.versions || [];
    if (finalVersions(c).length) return;   // 버튼이 비활성이지만 이중 방어
    const label = `${c.institution_display}${c.service_name ? ` · ${c.service_name}` : ""}`;
    const msg = versions.length
      ? `"${label}"을(를) 삭제하시겠습니까?\n\n작성중 버전 ${versions.length}개가 함께 삭제됩니다.\n되돌릴 수 없습니다.`
      : `"${label}"을(를) 삭제하시겠습니까?\n\n되돌릴 수 없습니다.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete(`quotation/cases/${c.id}/`);
      await load();
    } catch (err) {
      alert("삭제 실패: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  // ✅ JWT 전용 인증이라 window.open으로는 Authorization 헤더가 안 실려 401이 난다 —
  // api 인스턴스로 blob을 받아 저장한다(파일명은 Content-Disposition에서 추출).
  const handleExport = async (versionId) => {
    setBusy(true);
    try {
      await downloadFileWithAlert(
        `quotation/versions/${versionId}/export-xlsx/`,
        // ⚠️ fallback일 뿐이다 — 실제 이름은 서버가 Content-Disposition으로 준다
        // (quotation/views.py quotation_filename). 여기서 재작성하지 않는다.
        "견적서.xlsx",
        "견적서 다운로드에 실패했습니다."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.wrap}>
      <div style={S.headerRow}>
        <h2 style={S.title}>용역 견적서 관리</h2>
        <button style={S.newBtn} onClick={() => navigate("/quotation/new")}>+ 새 견적서 작성</button>
      </div>

      {cases.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "#9ca3af", border: "1px dashed #d1d5db", borderRadius: 8 }}>
          등록된 견적서가 없습니다.
        </div>
      )}

      {cases.map((c) => {
        const isOpen = !!expanded[c.id];
        const versions = c.versions || [];
        return (
          <div key={c.id} ref={(el) => { caseRefs.current[c.id] = el; }}
            style={{ ...S.caseCard,
              ...(highlightId === c.id
                ? { boxShadow: "0 0 0 2px #93c5fd", background: "#eff6ff",
                    transition: "background 0.4s, box-shadow 0.4s" }
                : { transition: "background 0.4s, box-shadow 0.4s" }) }}>
            <div style={S.caseHeader} onClick={() => toggle(c.id)}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{isOpen ? "▼" : "▶"}</span>
                <span style={S.caseTitle}>{c.institution_display}{c.service_name ? ` · ${c.service_name}` : ""}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span style={S.caseMeta}>버전 {versions.length}개</span>
                {/* ✅ 확정본이 하나라도 있으면 비활성 — 케이스를 지우면 cascade로 확정본까지
                    사라져 "확정본 삭제 차단" 원칙이 우회되기 때문(서버에서도 403으로 막는다). */}
                <button
                  type="button"
                  style={finalVersions(c).length ? S.caseDeleteBtnOff : S.caseDeleteBtn}
                  disabled={busy || finalVersions(c).length > 0}
                  title={finalVersions(c).length
                    ? `확정된 버전이 있어 삭제할 수 없습니다 (${finalVersions(c).map((v) => `v${v.version}`).join(", ")})`
                    : "이 발주처 견적서를 버전까지 모두 삭제"}
                  onClick={(e) => { e.stopPropagation(); handleDeleteCase(c); }}
                >
                  삭제
                </button>
              </div>
            </div>

            {isOpen && (
              <div style={S.tableScroll}>
              <table style={S.versionTable}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, ...S.colVersion }}>버전</th>
                    <th style={{ ...S.th, ...S.colStatus }}>상태</th>
                    <th style={{ ...S.th, ...S.colYear }}>연도</th>
                    {/* 품명·변경사항만 가변 — 둘 다 말줄임이라 좁아져도 안 깨진다. */}
                    <th style={{ ...S.th, ...S.colTitle }}>품명</th>
                    <th style={{ ...S.th, ...S.colPersonnel }}>인원/개월</th>
                    <th style={{ ...S.th, ...S.colUpdated }}>수정일</th>
                    {/* 변경사항이 유일한 가변 컬럼 — 남는 폭을 전부 가져간다. */}
                    <th style={{ ...S.th, width: "auto" }}>변경사항</th>
                    <th style={{ ...S.th, ...S.colAction }}></th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v.id}>
                      <td style={{ ...S.td, ...S.colVersion }}>v{v.version}</td>
                      <td style={{ ...S.td, ...S.colStatus }}>
                        <span style={v.status === "final" ? S.badgeFinal : S.badgeDraft}>
                          {v.status === "final" ? "확정" : "작성중"}
                        </span>
                      </td>
                      <td style={{ ...S.td, ...S.colYear }}>{v.year}</td>
                      <td style={{ ...S.td, ...S.colTitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v.title}>{v.title}</td>
                      {/* ⚠️ months는 DecimalField라 "12.00"으로 내려온다 — fmtMonths로 찍는다. */}
                      <td style={{ ...S.td, ...S.colPersonnel }}>{v.total_personnel}명 / {fmtMonths(v.months)}개월</td>
                      <td style={{ ...S.td, ...S.colUpdated }}>{(v.updated_at || "").slice(0, 10)}</td>
                      <td style={{ ...S.td, overflow: "hidden" }}>
                        <ChangeNoteCell value={v.change_note || ""}
                          onSave={(note) => saveChangeNote(v.id, note)} />
                      </td>
                      <td style={{ ...S.td, ...S.colAction, textAlign: "right" }}>
                        <button style={S.actionBtn} disabled={busy} onClick={() => navigate(`/quotation/${v.id}/edit`)}>
                          {v.status === "final" ? "보기" : "편집"}
                        </button>
                        <button style={S.actionBtn} disabled={busy} onClick={() => handleExport(v.id)}>엑셀</button>
                        <button style={S.actionBtnPrimary} disabled={busy} onClick={() => setNewVersionFor(v.id)}>새 버전</button>
                        {v.status !== "final" && (
                          <button style={S.actionBtnDanger} disabled={busy} onClick={() => handleDelete(v.id)}>삭제</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        );
      })}

      {newVersionFor != null && (
        <NewVersionModal
          busy={busy}
          onClose={() => setNewVersionFor(null)}
          onConfirm={(note) => createVersion(newVersionFor, note)}
        />
      )}
    </div>
  );
}


// ✅ 변경사항 메모 셀 — 평소엔 텍스트, 클릭하면 입력칸.
// ⚠️ 저장 성공 시 목록을 다시 부르지 않는다(부모가 로컬 상태만 갈아끼운다) —
// 리로드하면 펼쳐둔 발주처 카드가 접히고 스크롤이 튄다.
function ChangeNoteCell({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = React.useRef(null);
  // ⚠️ 저장 직후 blur가 한 번 더 들어와 같은 값을 또 PATCH하는 걸 막는다.
  const done = React.useRef(false);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  useEffect(() => { if (editing) { inputRef.current?.focus(); inputRef.current?.select(); } }, [editing]);

  const commit = async () => {
    if (done.current) return;
    done.current = true;
    const next = draft.slice(0, 200);
    if (next === value) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      alert("변경사항 저장 실패: " + (err?.response?.data?.detail || err.message));
      setDraft(value);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef} value={draft} maxLength={200} disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          // Esc는 취소 — done을 세워 뒤따르는 blur가 저장하지 않게 한다.
          if (e.key === "Escape") { done.current = true; setDraft(value); setEditing(false); }
        }}
        style={{ width: "100%", height: 26, boxSizing: "border-box", fontSize: 12,
                 padding: "0 6px", border: "1px solid #93c5fd", borderRadius: 4 }}
      />
    );
  }
  return (
    <div
      title={value || ""}
      onClick={() => { done.current = false; setEditing(true); }}
      style={{ cursor: "text", fontSize: 12, color: value ? "#374151" : "#9ca3af",
               overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
               minHeight: 18 }}
    >
      {value || "메모 추가"}
    </div>
  );
}


// ✅ 새 버전 생성 전 변경사항 입력 — 선택 입력이라 비워도 [확인]으로 그대로 생성된다.
// ⚠️ 배경 클릭 닫기는 useBackdropClose만 쓴다(오버레이에 onClick 직접 금지) —
// 입력칸에서 드래그하다 밖에서 손을 떼면 배경 클릭으로 오판돼 입력이 날아간다.
function NewVersionModal({ busy, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const backdrop = useBackdropClose(onClose);
  const inputRef = React.useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div {...backdrop}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex",
               alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "#fff", borderRadius: 10, padding: 20, width: 420, maxWidth: "92vw",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, whiteSpace: "nowrap" }}>새 버전 만들기</div>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
          무엇이 달라지는지 한 줄로 남겨두면 목록에서 버전을 구분하기 쉽습니다(선택).
        </div>
        <input
          ref={inputRef} value={note} maxLength={200} disabled={busy}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onConfirm(note); } }}
          placeholder="예: 시급 2027년 예상단가로 갱신"
          style={{ width: "100%", height: 36, boxSizing: "border-box", fontSize: 13, padding: "0 10px",
                   border: "1px solid #d1d5db", borderRadius: 6 }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={busy}
            style={{ width: "auto", padding: "0 14px", height: 32, fontSize: 13, whiteSpace: "nowrap",
                     border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
            취소
          </button>
          <button type="button" onClick={() => onConfirm(note)} disabled={busy}
            style={{ width: "auto", padding: "0 16px", height: 32, fontSize: 13, fontWeight: 600,
                     whiteSpace: "nowrap", border: "none", borderRadius: 6, background: "#0b5fff",
                     color: "#fff", cursor: "pointer" }}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
}
